import { useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { authenticatedApi } from '../../Api/api';
import DocumentViewer from '../DocumentViewer';
import { adaptToIDocument, AdaptedIDocument } from '../../utils/documentAdapter';
import { formatPrice } from '../../utils/priceHelpers';
import env from '../../config/env';

interface InvoiceButtonProps {
  contractId: number;
  invoiceType: 'payment_schedule' | 'one_time_stripe' | 'stripe_installments' | null;
  invoiceDocumentId?: number;
  hasInvoices: boolean;
  invoiceCount: number;
  className?: string;
}

interface BeneficiaryInvoice {
  id: string;
  issuer: 'stripe' | 'platform';
  invoiceType: 'payment_schedule' | 'one_time_payment' | 'installment';
  date: string;
  amount: number;
  isPaid: boolean;
  label: string;
  installmentNumber?: number;
}

/**
 * Reusable button component for displaying and downloading invoices
 * Displays different buttons based on invoice type:
 * - payment_schedule: "Télécharger échéancier" (opens modal)
 * - one_time_stripe: "Télécharger facture" (opens modal)
 * - stripe_installments: "Voir les factures" (opens modal with list)
 * - null/no invoices: Disabled button
 *
 * All invoice types now open in a modal for consistent UX
 */
export default function InvoiceButton({
  contractId,
  invoiceType,
  invoiceDocumentId: _invoiceDocumentId, // Keep for compatibility but not used anymore
  hasInvoices,
  invoiceCount,
  className = '',
}: InvoiceButtonProps) {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [invoices, setInvoices] = useState<AdaptedIDocument[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<AdaptedIDocument | null>(null);
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (!hasInvoices || !invoiceType || invoiceCount === 0) {
      return;
    }

    setLoading(true);
    try {
      const response = await authenticatedApi.get<{ contractId: number; invoices: BeneficiaryInvoice[] }>(
        `/invoice-beneficiary/${contractId}`
      );

      const fetchedInvoices = response.data.invoices;

      // Check if no invoices were found
      if (fetchedInvoices.length === 0) {
        alert(
          'Aucune facture disponible pour ce contrat.\n\n' +
          'Cela peut être dû à :\n' +
          '• Le document échéancier n\'a pas été généré\n' +
          '• Erreur de communication avec Stripe (clé API dev/prod)\n' +
          '• Aucune facture payée pour ce contrat'
        );
        return;
      }

      // Adapt invoices to IDocument format
      const adaptedInvoices = fetchedInvoices.map((invoice) =>
        adaptToIDocument({
          id: invoice.id,
          label: invoice.label,
          date: new Date(invoice.date),
          getViewUrl: async () => {
            // Fetch a short-lived download token
            const tokenResponse = await authenticatedApi.get<{ documentToken: string }>(
              `/invoice-beneficiary/${contractId}/get-invoice-token/${invoice.id}`
            );
            const token = tokenResponse.data.documentToken;

            return `${env.PUBLIC_API_URL}/invoice-beneficiary/${contractId}/download/${invoice.id}?downloadType=view&documentDownloadToken=${token}`;
          },
          getDownloadUrl: async () => {
            // Fetch a short-lived download token
            const tokenResponse = await authenticatedApi.get<{ documentToken: string }>(
              `/invoice-beneficiary/${contractId}/get-invoice-token/${invoice.id}`
            );
            const token = tokenResponse.data.documentToken;

            return `${env.PUBLIC_API_URL}/invoice-beneficiary/${contractId}/download/${invoice.id}?downloadType=download&documentDownloadToken=${token}`;
          },
          metadata: {
            amount: invoice.amount,
            isPaid: invoice.isPaid,
            installmentNumber: invoice.installmentNumber,
            invoiceType: invoice.invoiceType,
          },
        })
      );

      setInvoices(adaptedInvoices);
      setSelectedInvoice(adaptedInvoices[0] || null);
      setViewerOpen(true);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      alert('Erreur lors de la récupération des factures');
    } finally {
      setLoading(false);
    }
  };

  // Determine button text based on invoice type
  const getButtonText = () => {
    if (!hasInvoices || !invoiceType || invoiceCount === 0) {
      return 'Aucune facture disponible';
    }

    return `${invoiceCount} facture${invoiceCount > 1 ? 's' : ''}`;
  };

  // Custom render for invoice list items
  const renderInvoiceListItem = (doc: AdaptedIDocument) => {
    const metadata = doc._metadata || {};
    return (
      <div className="tw-flex tw-flex-col tw-gap-1">
        <div className="tw-font-medium tw-text-sm">{doc.label}</div>
        <div className="tw-text-xs tw-text-gray-600">{formatPrice(metadata.amount || 0)}</div>
        <div className="tw-flex tw-gap-2 tw-items-center">
          {metadata.isPaid && (
            <span className="tw-text-xs tw-px-2 tw-py-0.5 tw-bg-green-100 tw-text-green-700 tw-rounded">
              Payée
            </span>
          )}
          {metadata.installmentNumber && (
            <span className="tw-text-xs tw-px-2 tw-py-0.5 tw-bg-blue-100 tw-text-blue-700 tw-rounded">
              Mensualité {metadata.installmentNumber}
            </span>
          )}
        </div>
      </div>
    );
  };

  const isDisabled = !hasInvoices || !invoiceType || invoiceCount === 0 || loading;

  return (
    <>
      <button
        onClick={handleClick}
        disabled={isDisabled}
        className={`
          tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1.5 tw-text-xs tw-font-medium tw-rounded-lg
          tw-transition-colors
          ${
            isDisabled
              ? 'tw-bg-gray-100 tw-text-gray-400 tw-cursor-not-allowed'
              : 'tw-bg-affinicar-lighter tw-text-affinicar-primary hover:tw-bg-affinicar-lighter/80 tw-cursor-pointer'
          }
          ${className}
        `}
      >
        {loading ? <Loader2 size={14} className="tw-animate-spin" /> : <FileText size={14} />}
        <span>{isDisabled ? '-' : getButtonText()}</span>
      </button>

      {/* Document viewer modal for invoices */}
      <DocumentViewer
        document={selectedInvoice}
        documents={invoices}
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        onNavigate={setSelectedInvoice}
        title={`Factures du contrat #${contractId}`}
        getTokenUrl={() => ''} // Not used for adapted documents
        listMode={true}
        renderListItem={renderInvoiceListItem}
      />
    </>
  );
}
